import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  MenuItem,
} from "@mui/material";

import axios from "axios";
import SimpleTable from "../../components/SimpleTable";
import { tokens } from "../../theme";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const LendingReport = () => {
  const theme = useTheme();
  // Get today's date in YYYY-MM-DD format
const today = dayjs().format("YYYY-MM-DD");
  const colors = tokens(theme.palette.mode);

  const [lendings, setLendings] = useState([]);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [count, setCount] = useState(0);

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [userId, setUserId] = useState("");
  const [itemId, setItemId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");

  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch users (only sales role)
const fetchUsers = useCallback(async () => {
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user`);
    const allUsers = res.data || [];

    const filtered = allUsers.filter(
      (u) => u.Role?.name?.toLowerCase().includes("sale")
    );

    setUsers(filtered);
  } catch (err) {
    console.error("Error fetching users:", err);
    setUsers([]);
  }
}, []);


  // Fetch items
  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/item`);
      setItems(res.data || []);
    } catch (err) {
      console.error("Error fetching items:", err);
      setItems([]);
    }
  }, []);

  // Fetch warehouses
  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/warehouse`);
      setWarehouses(res.data || []);
    } catch (err) {
      console.error("Error fetching warehouses:", err);
      setWarehouses([]);
    }
  }, []);

  // Load dropdown data on mount
  useEffect(() => {
    fetchUsers();
    fetchItems();
    fetchWarehouses();
  }, [fetchUsers, fetchItems, fetchWarehouses]);

  // Fetch lending report
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        startDate,
        endDate,
        userId: userId || undefined,
        itemId: itemId || undefined,
        warehouseId: warehouseId || undefined,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/lending/report`,
        payload
      );

      setLendings(res.data.report || []);
      setTotalQuantity(res.data.totalQuantity || 0);
      setCount(res.data.count || 0);
    } catch (err) {
      console.error("Error fetching lending report:", err);
      setLendings([]);
      setTotalQuantity(0);
      setCount(0);
    }
    setLoading(false);
  }, [startDate, endDate, userId, itemId, warehouseId]);


  useEffect(() => {
  fetchReport();
}, [fetchReport]);

  const columns = [
    { field: "user", headerName: "User", width: 180 },
    { field: "item", headerName: "Item", width: 180 },
    { field: "warehouse", headerName: "Warehouse", width: 180 },
    { field: "quantity", headerName: "Quantity", width: 120 },
    {
      field: "lendingDate",
      headerName: "Lending Date",
      width: 150,
      valueFormatter: ({ value }) => dayjs.utc(value).format("YYYY-MM-DD"),
    },
  ];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  return (
    <Box p={3}>
      <Typography variant="h4" mb={2}>
        Lending Report
      </Typography>

      {/* Filters */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        {/* User dropdown */}
        <TextField
          select
          label="Sales User"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {users.map((user) => (
            <MenuItem key={user.id} value={user.id}>
               {user.fullName || user.userName}
            </MenuItem>
          ))}
        </TextField>

        {/* Item dropdown */}
        <TextField
          select
          label="Item"
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {items.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Warehouse dropdown */}
        <TextField
          select
          label="Warehouse"
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {warehouses.map((wh) => (
            <MenuItem key={wh.id} value={wh.id}>
              {wh.name}
            </MenuItem>
          ))}
        </TextField>

        <Button variant="contained" onClick={fetchReport} disabled={loading}>
          {loading ? "Loading..." : "Generate Report"}
        </Button>
      </Box>

      {/* Summary */}
      <Box mb={2}>
        <Typography>Total Quantity Lent: {totalQuantity}</Typography>
        {/* <Typography>Total Records: {count}</Typography> */}
      </Box>

      {/* Table */}
      <SimpleTable
        rows={lendings}
        columns={columns}
        getRowId={(row) => row.id}
        loading={loading}
        pageSizeOptions={[5, 10, 25]}
        height="60vh"
        sx={gridSx}
      />
    </Box>
  );
};

export default LendingReport;
